import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Container,
  Grid,
  Button,
  FormControl,
  TextField,
  Autocomplete,
  IconButton,
  Link,
  Alert,
  Typography,
  Checkbox
} from "@mui/material";
import { EditOutlined } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import moment from "moment";

import { addContract, getContract, updateContract } from "../../services/backend/ContractService";
import { annexesGroup } from "../../services/backend/CorrespondenceService";
import {
  extractContractData,
  extractStartupData,
  parseAiDate,
  parseAiNumber,
} from "../../services/backend/ContractExtractionService";
import { CONTRACT_STATUS, getSupervisionFlow, PERMISSIONS, validateContractCodeForUser } from "../../constants";
import {
  getContractConfig,
  DEFAULT_CONTRACT_CONFIG,
  dependencyOptions,
  natureOptionsForCode,
  modalityOptions,
  typesForModality,
  allDependencyEntries,
  allNatureEntriesForCode,
  allModalityEntries,
  allTypeEntriesForModality,
  isInactiveValue,
} from "../../services/backend/ContractConfigService";
import { debugLog, getContractField } from '../../functions';
import CircularProgress from "../generic/CircularProgress";
import PaymentPlanModal from "./PaymentPlanModal";
import TasksModal from "./TasksModal";
import MyBackdrop from "../generic/MyBackdrop";
import SelectUser from "../generic/SelectUser";
import MoneyInput from "../generic/MoneyInput";
import MySnackbar from "../generic/MySnackbar";
import AnnexesUploader from "../generic/AnnexesUploader";
import DialogConfirmation from "../generic/DialogConfirmation";
import ContractNews from "./ContractNews";
import ContractFormPanel from "./ContractFormPanel";

const contractTypes = [
  'Contrato de prestación de servicios',
  'Contrato a término fijo menor a 1 año',
  'Contrato a término indefinido'
];

const ContractForm = () => {
  const navigate = useNavigate();
  const isInitialMount = useRef(undefined);
  // Snapshot de los apoyos persistidos en Firestore: aporta correo y datos de
  // perfil de los apoyos retirados para poder notificarles el retiro.
  const persistedSupports = useRef([]);
  const user = useSelector((state) => state.user.value);
  const users = useSelector((state) => state.user.users);
  const { contractId } = useParams();
  const [approvedAt, setApprovedAt] = useState(null);
  const [arlLevel, setArlLevel] = useState('L1');
  const [contractType, setContractType] = useState('');
  const [contractCode, setContractCode] = useState('');
  const [contractSubject, setContractSubject] = useState('');
  const [contractBudget, setContractBudget] = useState('');
  const [contractPeriod, setContractPeriod] = useState('');
  const [contractor, setContractor] = useState();
  const [contractorNuip, setContractorNuip] = useState('');
  const [dependency, setDependency] = useState(null);
  const [documentText, setDocumentText] = useState();
  // Catálogos administrables (config/contracts); las semillas garantizan el
  // comportamiento actual mientras carga o si el doc no existe.
  const [catalogCfg, setCatalogCfg] = useState(DEFAULT_CONTRACT_CONFIG);
  const [modality, setModality] = useState(null);
  const [modalityContractType, setModalityContractType] = useState(null);
  const [extractedData, setExtractedData] = useState();
  const [fileUrl, setFileUrl] = useState('');
  const [fileHashes, setFileHashes] = useState({});
  const [startupUrl, setStartupUrl] = useState(null);
  const [designationUrl, setDesignationUrl] = useState(null);
  const [liquidationUrl, setLiquidationUrl] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [nature, setNature] = useState(null);
  const [news, setNews] = useState([]);
  const [paymentPlan, setPaymentPlan] = useState();
  const [salary, setSalary] = useState('');
  const [startedAt, setStartedAt] = useState(null);
  const [secopUrl, setSecopUrl] = useState('');
  const [isSecopUrlValid, setIsSecopUrlValid] = useState(false);
  const [status, setStatus] = useState(CONTRACT_STATUS.DRAFT);
  const [supervisors, setSupervisors] = useState([]);
  const [supervisorIDs, setSupervisorIDs] = useState([]);
  const [supervisorNotified, setSupervisorNotified] = useState(false);
  const [supports, setSupports] = useState([]);
  const [supportIDs, setSupportIDs] = useState([]);
  // Apoyos a los que ya se les envió el correo de asignación (flag persistido).
  const [supportNotifiedIDs, setSupportNotifiedIDs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [adminContractId, setAdminContractId] = useState('');
  const [showPaymentPlanModal, setShowPaymentPlanModal] = useState(false);
  const [showTasksModal, setShowTasksModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState();
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [modified, setModified] = useState(undefined);
  const [confirmInfo, setConfirmInfo] = useState({ open: false });
  const [closeContractInfo, setCloseContractInfo] = useState({ open: false });
  const [sendSupervisorInfo, setSendSupervisorInfo] = useState({ open: false });
  const [annexes, setAnnexes] = useState([]);
  const [rejectContractMessage, setRejectContractMessage] = useState('');
  const [shouldSendNotification, setShouldSendNotification] = useState(true);

  // El apoyo asignado tiene los mismos alcances de edición del supervisor,
  // en cualquier estado del contrato.
  const isAssignedSupport = supportIDs.includes(user.userId);

  useEffect(() => {
    (async () => setCatalogCfg(await getContractConfig()))();
  }, []);

  // Dependencia, Naturaleza y Modalidad/Tipo: el gestor (CONTRACT.UPDATE) los
  // diligencia desde DRAFT; en SUPERVISOR conservan la edición quienes ya
  // acceden al formulario en ese estado (supervisor efectivo / apoyo asignado).
  const canEditClassification =
    [CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status) &&
    (status === CONTRACT_STATUS.SUPERVISOR || user.permissions.includes(PERMISSIONS.CONTRACT.UPDATE));

  // Opciones habilitadas del catálogo + el valor ya guardado aunque esté
  // desactivado o renombrado (los contratos persisten el texto).
  const withCurrent = (opts, current) =>
    current && !opts.includes(current) ? [...opts, current] : opts;

  const dependencyOpts = useMemo(
    () => withCurrent(dependencyOptions(catalogCfg), dependency),
    [catalogCfg, dependency]
  );
  const natureOpts = useMemo(
    () => withCurrent(natureOptionsForCode(catalogCfg, contractCode), nature),
    [catalogCfg, contractCode, nature]
  );
  const modalityOpts = useMemo(
    () => withCurrent(modalityOptions(catalogCfg), modality),
    [catalogCfg, modality]
  );
  const modalityTypeOpts = useMemo(
    () => withCurrent(typesForModality(catalogCfg, modality), modalityContractType),
    [catalogCfg, modality, modalityContractType]
  );

  const inactiveLabel = (entries) => (option) =>
    isInactiveValue(entries, option) ? `${option} (inactivo)` : option;

  // Autollenado con IA del PDF del contrato. El prompt y la llamada viven en
  // ContractExtractionService (compartido con el formulario jurídico).
  const handleGetData = async () => {
    if (!documentText) return debugLog('No hay texto de documento');
    setOpenBackdrop("Extrayendo información del contrato");

    const data = await extractContractData(documentText);
    setOpenBackdrop(undefined);

    if (!data) {
      return setInfoSnack({
        open: true,
        message: 'No fue posible extraer la información del contrato: diligencia los campos manualmente.',
        severity: 'warning',
        duration: 6000,
      });
    }

    setExtractedData(data);
  };

  const handleGetDataStartup = async (text) => {
    setOpenBackdrop("Extrayendo información del acta de inicio");

    const data = await extractStartupData(text);
    setOpenBackdrop(undefined);

    if (!data) {
      return setInfoSnack({
        open: true,
        message: 'No fue posible extraer la información del acta de inicio: diligencia los campos manualmente.',
        severity: 'warning',
        duration: 6000,
      });
    }

    const startedAtValue = parseAiDate(data.startedAt);
    const finishedAtValue = parseAiDate(data.finishedAt);
    const period = parseAiNumber(data.contractPeriod);

    if (startedAtValue) setStartedAt(startedAtValue);
    if (period) setContractPeriod(period);
    if (finishedAtValue) setFinishedAt(finishedAtValue);
  };

  const buildSupervisorIDs = () => {
    const ids = [...supervisorIDs];
    news
      .filter((n) => ["CHANGE_SUPERVISOR", "ASSIGN_SUPERVISOR"].includes(n.type) && n.supervisor)
      .map((n) => n.supervisor.userId)
      .forEach((id) => {
        if (!ids.includes(id)) ids.push(id);
      });

    return ids;
  };

  // Fuente única del payload: el snapshot completo del formulario. Todas las
  // transiciones (guardar, enviar a supervisor, enviar a contratista) escriben
  // por aquí para que ningún cambio de estado se publique dejando fuera lo que
  // el usuario acaba de editar. Los campos de apoyos quedan fuera a propósito:
  // los administra handleSubmit junto con el flag de notificación.
  const buildContractData = (overrides = {}) => ({
    annexes,
    approvedAt,
    arlLevel,
    contractType,
    contractCode,
    contractSubject,
    contractBudget,
    contractPeriod,
    contractor,
    contractorNuip,
    dependency,
    documentText,
    finishedAt,
    fileUrl,
    fileHashes,
    liquidationUrl,
    nature,
    modality,
    modalityContractType,
    news,
    paymentPlan,
    salary,
    startedAt,
    startupUrl,
    designationUrl,
    status,
    secopUrl,
    supervisors,
    supervisorIDs: buildSupervisorIDs(),
    supervisorNotified,
    tasks,
    ...overrides,
  });

  const handleSubmit = async () => {
    let message;

    if (!fileUrl) message = "Debes cargar el contracto";
    if (!contractCode) message = "Debes escribir el código de contrato";
    if (message) return setInfoSnack({ open: true, message, severity: 'warning' });

    const data = buildContractData({ supports, supportIDs, supportNotifiedIDs });

    if (contractId) {
      setOpenBackdrop("Guardando contrato");

      // Idempotencia por flag persistido (supportNotifiedIDs): se notifica la
      // asignación a los apoyos que aún no figuran en el flag y el retiro a
      // los que salieron de supportIDs. Los retirados quedan fuera del flag
      // para volver a notificarlos si se reasignan; los agregados sin correo
      // no se marcan y quedan pendientes de notificación.
      const notifiedIDs = supportNotifiedIDs.filter((id) => supportIDs.includes(id));
      const addedSupports = (supports || []).filter(
        (s) => s?.email && !supportNotifiedIDs.includes(s.userId)
      );
      const removedSupports = persistedSupports.current.filter(
        (s) => supportNotifiedIDs.includes(s.userId) && !supportIDs.includes(s.userId)
      );

      const emails = [];
      if (addedSupports.length) {
        const effectiveSupervisor = getContractField(news, data)('supervisors')?.[0];
        const assigner = user.roles.includes('ADMIN') && !supervisorIDs.includes(user.userId)
          ? user
          : (effectiveSupervisor || user);
        emails.push(...addedSupports.map((s) => ({
          emailTo: s.email,
          template: 'supportAssignmentNotification',
          data: {
            contractCode,
            usertitle: s.usertitle,
            fullname: `${s.name} ${s.lastName}`,
            position: s.position,
            entity: s.entity,
            dependence: s.dependence,
            contractorName: contractor ? `${contractor.name} ${contractor.lastName}` : 'Sin asignar',
            supervisorName: `${assigner.name} ${assigner.lastName}`,
          },
        })));
        notifiedIDs.push(...addedSupports.map((s) => s.userId));
      }

      emails.push(...removedSupports.filter((s) => s?.email).map((s) => ({
        emailTo: s.email,
        template: 'supportRemovalNotification',
        data: {
          contractCode,
          usertitle: s.usertitle,
          fullname: `${s.name} ${s.lastName}`,
          position: s.position,
          entity: s.entity,
          dependence: s.dependence,
          contractorName: contractor ? `${contractor.name} ${contractor.lastName}` : 'Sin asignar',
        },
      })));

      if (emails.length) data.email = emails;
      data.supportNotifiedIDs = notifiedIDs;

      const response = await updateContract(contractId, data, user);
      if (response) {
        setSupportNotifiedIDs(notifiedIDs);
        persistedSupports.current = [...(supports || [])];
      }
    } else {

      // Validar formato de contractCode
      setOpenBackdrop("Validando contrato");
      if (contractCode) {
        message = validateContractCodeForUser(contractCode, user) || undefined;
      }

      const existContract = await getContract(contractCode, user);
      if (existContract) {
        message = `Ya existe un contrato con el código ${contractCode}`;
      }

      if (message) {
        setOpenBackdrop(undefined);
        return setInfoSnack({ open: true, message, severity: 'warning' });
      }

      setOpenBackdrop("Guardando contrato");
      const response = await addContract({ ...data, userId: user.userId }, user);
      if (!response) {
        setInfoSnack({ open: true, message: "Error al crear el contrato", severity: 'error' });
        setOpenBackdrop(undefined);
        return;
      }

      if (response.fileUrl) setFileUrl(response.fileUrl);
      if (response.startupUrl) setStartupUrl(response.startupUrl);
      if (response.designationUrl) setDesignationUrl(response.designationUrl);
      setAdminContractId(user.userId);
      navigate(`/contracts/${contractCode}`, { replace: true });
    }
    setModified(false);
    setOpenBackdrop(undefined);
    setLoading(false);
  };

  const handleSendSupervisor = async () => {
    if (!supervisors.length) {
      setInfoSnack({ open: true, message: 'Debes seleccionar al menos un supervisor', severity: 'warning' });
      return;
    }

    await handleSubmit();
    setOpenBackdrop("Enviando a supervisor...");

    if (!supervisorNotified && shouldSendNotification) {
      const props = {
        fecha: moment().format("D [de] MMMM, YYYY"),
        contract_code: contractCode,
        contract_contractor: contractor ? `${contractor.name} ${contractor.lastName}` : 'Sin asignar',
        titulo: supervisors[0].usertitle,
        nombre_destinatario: supervisors[0].name,
        apellido_destinatario: supervisors[0].lastName,
        cargo_destinatario: supervisors[0].position,
        dependencia_destinatario: supervisors[0].dependence,
        entidad_destinatario: supervisors[0].entity,
        recipient: supervisors[0],
        owner: user
      }
  
      fetch(
        `${process.env.REACT_APP_CONTAINER}/Document/handlePdfProcessFromFlow`,
        {
          method: "post",
          body: JSON.stringify(getSupervisionFlow(props)),
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + user.token,
          },
        }
      );
    }

    const activeSupervisor = getContractField(news, { supervisors, status })('supervisors')[0];

    const email = [
      {
        emailTo: activeSupervisor.email,
        template: 'contractSupervisorCheck',
        data: {
          contractCode,
          usertitle: activeSupervisor.usertitle,
          fullname: `${activeSupervisor.name} ${activeSupervisor.lastName}`,
          position: activeSupervisor.position,
          entity: activeSupervisor.entity,
          dependence: activeSupervisor.dependence,
          contractorName: contractor ? `${contractor.name} ${contractor.lastName}`: 'Sin asignar'
        }
      },
      {
        emailTo: user.email,
        template: 'contractSupervisorNotified',
        data: {
          contractCode,
          usertitle: user.usertitle,
          fullname: `${user.name} ${user.lastName}`,
          position: user.position,
          entity: user.entity,
          dependence: user.dependence,
          supervisorName: `${activeSupervisor.name} ${activeSupervisor.lastName}`,
          contractorName: contractor ? `${contractor.name} ${contractor.lastName}`: 'Sin asignar'
        }
      },
    ];

    // `supervisorNotified: true` se persiste de verdad: el objeto anterior
    // repetía la clave y la segunda (el estado aún en false) ganaba, así que
    // el flag nunca llegaba a Firestore y la notificación podía repetirse.
    const data = buildContractData({
      status: CONTRACT_STATUS.SUPERVISOR,
      supervisorNotified: true,
      email,
    });

    await updateContract(contractId, data, user);
    setStatus(CONTRACT_STATUS.SUPERVISOR);
    setSupervisorNotified(true);
    setOpenBackdrop(undefined);
  };

  const handleSendContractor = async () => {
    if (paymentPlan === undefined || tasks.length === 0 || !dependency || !nature) {
      const missingFields = [];
      if (!paymentPlan) missingFields.push('Plan de pagos');
      if (!tasks.length) missingFields.push('Obligaciones del contratista');
      if (!dependency) missingFields.push('Dependencia');
      if (!nature) missingFields.push('Naturaleza del contrato');

      const message = (
        <div>
          <div>Debes completar los siguientes campos:</div>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {missingFields.map((field, index) => (
              <li key={index}>{field}</li>
            ))}
          </ul>
        </div>
      );

      setInfoSnack({ open: true, message, severity: 'warning' });
      return;
    }

    setOpenBackdrop("Enviando a Contratista");

    const email = {
      emailTo: contractor.email,
      template: 'contractSupervisorApproved',
      data: {
        contractCode: contractId,
        usertitle: contractor.usertitle,
        fullname: `${contractor.name} ${contractor.lastName}`,
        position: contractor.position,
        entity: contractor.entity,
        dependence: contractor.dependence,
        supervisorName: `${supervisors[0].name} ${supervisors[0].lastName}`
      }
    };

    // Se guarda el snapshot completo, no solo el estado: la validación de
    // arriba corre contra el formulario en memoria, donde el supervisor pudo
    // configurar el plan de pagos (u obligaciones, dependencia, naturaleza)
    // sin pulsar "Actualizar". Escribiendo solo `status`, el contrato quedaba
    // aprobado y sin plan de pagos.
    const response = await updateContract(
      contractId,
      buildContractData({ status: CONTRACT_STATUS.APPROVED, email }),
      user
    );

    if (!response) {
      setOpenBackdrop(undefined);
      return setInfoSnack({
        open: true,
        message: "Error al enviar el contrato al contratista",
        severity: 'error',
      });
    }

    setStatus(CONTRACT_STATUS.APPROVED);
    setModified(false);
    setOpenBackdrop(undefined);
  };

  const handleRejectContract = async ({ message }) => {
    setOpenBackdrop("Devolviendo contracto");
    const contractAdmin = users.find(u => u.userId === adminContractId);

    const email = {
      emailTo: contractAdmin.email,
      template: 'contractSupervisorRejected',
      data: {
        contractCode: contractId,
        usertitle: contractAdmin.usertitle,
        fullname: `${contractAdmin.name} ${contractAdmin.lastName}`,
        position: contractAdmin.position,
        entity: contractAdmin.entity,
        dependence: contractAdmin.dependence,
        supervisorName: `${supervisors[0].name} ${supervisors[0].lastName}`,
        message,
      }
    };

    // Mismo criterio que el envío a contratista: se persiste el snapshot
    // completo, no solo el estado, para que lo que el supervisor editó sin
    // pulsar "Actualizar" (plan de pagos, obligaciones, dependencia…) llegue
    // al gestor junto con la devolución en vez de perderse.
    const response = await updateContract(
      contractId,
      buildContractData({ status: CONTRACT_STATUS.DRAFT, email }),
      user
    );

    if (!response) {
      setOpenBackdrop(undefined);
      return setInfoSnack({
        open: true,
        message: "Error al devolver el contrato",
        severity: 'error',
      });
    }

    setStatus(CONTRACT_STATUS.DRAFT);
    setModified(false);
    setOpenBackdrop(undefined);
    navigate('/contracts/supervisor');
  }

  const handleSecopUrlBlur = (evt) => {
    const urlPattern = /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/i; // eslint-disable-line

    if (urlPattern.test(evt.target.value)) {
      setIsSecopUrlValid(true);
    } else {
      setSecopUrl('');
      setIsSecopUrlValid(false);
      setInfoSnack({ open: true, message: 'URL inválida', severity: 'warning' });
    }
  };

  const handleCloseContract = async() => {
    setOpenBackdrop("Cerrando contracto");
    await updateContract(contractId, { status: CONTRACT_STATUS.CLOSED }, user);
    if (user.roles.includes('ADMIN_CONTRACT')) {
      navigate('/contracts');
    } else if (user.roles.includes('SUPERVISOR_CONTRACT')) {
      navigate('/contracts/supervisor');
    }
    setStatus(CONTRACT_STATUS.CLOSED);
    setOpenBackdrop(undefined);
  }

  const getContractFieldWithNews = useCallback(getContractField(
    news,
    {
      status,
      finishedAt,
      contractPeriod,
      contractBudget,
      paymentPlan,
      startedAt,
      supervisors,
      salary,
      tasks
    }
  ), [news, status, finishedAt, contractPeriod, contractBudget, paymentPlan, startedAt, supervisors, salary, tasks]);

  // Aplica al formulario los datos extraídos por la IA. Cada campo se valida
  // antes de asignarse: el modelo devuelve null en lo que no identifica, y
  // moment(null/undefined) daría "hoy" — una fecha plausible y equivocada.
  useEffect(() => {
    if (!extractedData || contractCode) return;

    const data = extractedData;
    const period = parseAiNumber(data.contractPeriod);
    const budget = parseAiNumber(data.contractBudget);
    const monthlyFee = parseAiNumber(data.salary);
    const approvedAtValue = parseAiDate(data.approvedAt);
    const finishedAtValue = parseAiDate(data.finishedAt);

    if (data.contractType) setContractType(data.contractType);
    if (data.contractCode) setContractCode(String(data.contractCode).trim());
    if (data.contractSubject) setContractSubject(data.contractSubject);
    if (budget) setContractBudget(budget);
    if (monthlyFee) setSalary(monthlyFee);
    if (period) setContractPeriod(period);
    if (approvedAtValue) setApprovedAt(approvedAtValue);
    if (finishedAtValue) setFinishedAt(finishedAtValue);
    // Fecha de inicio: el contrato no la trae, se deriva del cierre menos el plazo.
    if (finishedAtValue && period) setStartedAt(finishedAtValue.clone().subtract(period, 'days'));
    if (data.contractorNuip) {
      setContractorNuip(data.contractorNuip);
      const contt = users.find(u => u.nuip == data.contractorNuip); // eslint-disable-line
      if (contt) setContractor(contt);
    }
  }, [extractedData]); // eslint-disable-line

  useEffect(() => {
    if (documentText && !contractId && !extractedData) handleGetData();
  }, [documentText]); // eslint-disable-line

  useEffect(() => {
    setModified(true);
    // eslint-disable-next-line
  }, [approvedAt, arlLevel, contractType, contractCode, contractSubject, contractBudget, contractPeriod,
    contractor, contractorNuip, dependency, fileUrl, fileHashes, finishedAt, nature, news, paymentPlan, salary,
    startedAt, startupUrl, designationUrl, supervisors, tasks]);

  useEffect(() => {
    if (liquidationUrl && status !== CONTRACT_STATUS.CLOSED) {
      setCloseContractInfo({ open: true, title: "¿Deseas cerrar este contrato?" });
    }
  }, [liquidationUrl]); // eslint-disable-line

  // Al cambiar el prefijo del código (DEX vs. no DEX), el listado de naturalezas válido cambia.
  // Si la naturaleza seleccionada ya no pertenece al listado vigente, se limpia.
  useEffect(() => {
    if (nature && !natureOptionsForCode(catalogCfg, contractCode).includes(nature)) {
      setNature(null);
    }
  }, [contractCode]); // eslint-disable-line

  useEffect(() => {
    if ((!contractId && !user.permissions.includes(PERMISSIONS.CONTRACT.CREATE))
      || (contractId && !user.permissions.includes(PERMISSIONS.CONTRACT.UPDATE)
        && !user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.UPDATE)
      )) return navigate('/home');

    (async () => {
      if (contractId && !contractCode) {
        const response = await getContract(contractId, user);
        if (!response) return navigate("/home");

        // Supervisor efectivo según las novedades (CHANGE_SUPERVISOR permanente,
        // ASSIGN_SUPERVISOR solo con ventana activa): un supervisor reemplazado
        // no accede a la edición aunque siga en supervisorIDs (histórico).
        const effectiveSupervisors = getContractField(response.news || [], response)('supervisors') || [];
        const isEffectiveSupervisor = effectiveSupervisors.some((s) => s?.userId === user.userId);
        // El apoyo asignado edita en los mismos estados que el supervisor
        // (todos menos DRAFT, bloqueado abajo para ambos).
        const isSupportEditor = response.supportIDs?.includes(user.userId);

        if (!user.permissions.includes(PERMISSIONS.CONTRACT.UPDATE)
          && (
            response.status === CONTRACT_STATUS.DRAFT
            || (!isEffectiveSupervisor && !isSupportEditor)
          )) return navigate('/home');

        setApprovedAt(response.approvedAt);
        setArlLevel(response.arlLevel);
        setContractType(response.contractType);
        setContractCode(response.contractCode);
        setContractSubject(response.contractSubject);
        setContractBudget(response.contractBudget);
        setContractPeriod(response.contractPeriod);
        setContractor(response.contractor);
        setContractorNuip(response.contractorNuip);
        setDependency(response.dependency);
        setDocumentText(response.documentText);
        setFinishedAt(response.finishedAt);
        setFileUrl(response.fileUrl);
        setFileHashes(response.fileHashes);
        setLiquidationUrl(response.liquidationUrl);
        setNature(response.nature);
        setModality(response.modality || null);
        setModalityContractType(response.modalityContractType || null);
        setNews(response.news || []);
        setPaymentPlan(response.paymentPlan);
        setSalary(response.salary);
        setStartedAt(response.startedAt);
        setStartupUrl(response.startupUrl);
        setDesignationUrl(response.designationUrl);
        setStatus(response.status);
        setSecopUrl(response.secopUrl);
        setIsSecopUrlValid(!!response.secopUrl);
        setSupervisors(response.supervisors);
        setSupervisorIDs(response.supervisorIDs);
        setSupervisorNotified(response.supervisorNotified);
        setSupports(response.supports || []);
        setSupportIDs(response.supportIDs || []);
        // Migración lazy: contratos anteriores al flag quedan sembrados con
        // sus apoyos actuales (ya notificados por el mecanismo previo de diff),
        // así el primer guardado no re-notifica a nadie.
        setSupportNotifiedIDs(response.supportNotifiedIDs || response.supportIDs || []);
        persistedSupports.current = response.supports || [];
        setTasks(response.tasks);
        setAdminContractId(response.userId);
        setAnnexes(response.annexes);
      }
      isInitialMount.current = true;
      setTimeout(() => setModified(false), 500);
    })();
  }, []); // eslint-disable-line

  return (
    <Container maxWidth={false}>
      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />

      <MyBackdrop open={!!openBackdrop} title={openBackdrop} />

      <PaymentPlanModal
        open={showPaymentPlanModal}
        onClose={() => setShowPaymentPlanModal(false)}
        setArlLevel={setArlLevel}
        setPaymentPlan={setPaymentPlan}
        contract={{
          approvedAt,
          arlLevel,
          contractType,
          contractCode,
          contractSubject,
          contractBudget, //: getContractFieldWithNews('contractBudget'),
          contractPeriod, //: getContractFieldWithNews('contractPeriod'),
          contractor,
          contractorNuip,
          dependency,
          fileUrl,
          finishedAt, //: getContractFieldWithNews('finishedAt'),
          nature,
          news,
          paymentPlan, //: getContractFieldWithNews('paymentPlan'),
          startedAt,
          startupUrl,
          designationUrl,
          salary,
          status
        }}
      />

      <TasksModal
        open={showTasksModal}
        onClose={() => setShowTasksModal(false)}
        setTasks={setTasks}
        contract={{ documentText, tasks, status, news }}
        setOpenBackdrop={setOpenBackdrop}
        getContractFieldWithNews={getContractFieldWithNews}
        noEdit={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
      />

      <DialogConfirmation
        confirmInfo={confirmInfo}
        content={(
          <TextField
            fullWidth
            multiline
            size="small"
            rows={3}
            placeholder="¿Por qué deseas devolver el contrato?"
            onChange={(evt) => setRejectContractMessage(evt.target.value)}
          />
        )}
        handleCloseDialog={(result) => {
          if (result) handleRejectContract({ message: rejectContractMessage });
          setConfirmInfo({ open: false });
          setRejectContractMessage('');
        }}
      />

      <DialogConfirmation
        confirmInfo={sendSupervisorInfo}
        content={!supervisorNotified && (
          <Box display='flex' alignItems='center' gap={2}>
            <Typography variant="body2">
              ¿Crear un radicado de notificación?
            </Typography>
            <Checkbox
              checked={shouldSendNotification}
              onChange={(evt) => setShouldSendNotification(evt.target.checked)}
            />
          </Box>
        )}
        handleCloseDialog={(result) => {
          if (result) handleSendSupervisor();
          setSendSupervisorInfo({ open: false });
        }}
      />

      <DialogConfirmation
        confirmInfo={closeContractInfo}
        handleCloseDialog={(result) => {
          if (result) handleCloseContract();
          setCloseContractInfo({ open: false });
        }}
      />

      <Grid container sx={{ mb: 1, height: 'calc(100vh - 80px)' }}>
        <Grid item xs={6} sx={{ height: '100%', overflowY: 'auto', padding: 2 }}>
          <Box component="form" onSubmit={handleSubmit} sx={{ m: 1 }}>
            <Grid container spacing={2}>
              {getContractFieldWithNews('status') === CONTRACT_STATUS.SUSPENDED
                && <Alert severity="info" sx={{ my: 2, margin: 'auto' }}>
                  Contrato Suspendido
                </Alert>
              }
              {getContractFieldWithNews('status') === CONTRACT_STATUS.FINISHED
                && <Alert severity="warning" sx={{ my: 2, margin: 'auto' }}>
                  Contrato Terminado
                </Alert>
              }
              {getContractFieldWithNews('status') === CONTRACT_STATUS.CLOSED
                && <Alert severity="warning" sx={{ my: 2, margin: 'auto' }}>
                  Contrato Cerrado
                </Alert>
              }
              <Grid item xs={10} sx={{ color: 'gray' }}>Información de contrato</Grid>
              {user.roles.includes('ADMIN')
                &&
                  <Grid item xs={2}>
                  <Box sx={{
                    backgroundColor: '#e3eed9ff',
                    borderRadius: 5,
                    color: 'gray', p: 1,
                    textAlign: 'center',
                    fontSize: '0.6rem',
                  }}>
                    {getContractFieldWithNews('status')}
                  </Box>
                </Grid>
              }
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <SelectUser
                    disabled={status !== CONTRACT_STATUS.DRAFT}
                    noedit={true}
                    userType="users"
                    filter="SUPERVISOR_CONTRACT"
                    placeholder="Supervisores de contrato"
                    multiple
                    value={getContractFieldWithNews('supervisors')}
                    onChange={(__, supers) => {
                      setSupervisors(supers);
                      setSupervisorIDs(supers.map(s => s.userId));
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <SelectUser
                    disabled={
                      ![CONTRACT_STATUS.SUPERVISOR, CONTRACT_STATUS.APPROVED].includes(status)
                      || (!supervisorIDs.includes(user.userId) && !user.roles.includes('ADMIN')
                        && !isAssignedSupport)
                    }
                    noedit={true}
                    userType="users"
                    filter="SUPPORT_SUPERVISION"
                    placeholder="Apoyo a la Supervisión"
                    multiple
                    value={supports}
                    onChange={(__, sups) => {
                      setSupports(sups);
                      setSupportIDs(sups.map(s => s.userId));
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    value={contractType}
                    options={contractTypes}
                    getOptionLabel={(option) => option}
                    onChange={(__, newValue) => {
                      setContractType(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de contrato"
                        placeholder="Tipo de contrato"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <TextField
                    required
                    label="Código de contrato"
                    value={contractCode}
                    InputProps={{ readOnly: !!contractId }}
                    onChange={(evt) => setContractCode(evt.target.value.trim())}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de firma"
                    value={approvedAt}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    onChange={(val) => setApprovedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <MoneyInput
                    label="Valor de contrato"
                    value={getContractFieldWithNews('contractBudget')}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    onChange={(evt) => {
                      if ([CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)) {
                        setContractBudget(evt.target.value);
                      }
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <MoneyInput
                    label="Honorarios Mensual"
                    value={salary}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    onChange={(evt) => setSalary(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de inicio"
                    value={startedAt}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    onChange={(val) => setStartedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de cierre"
                    value={getContractFieldWithNews('finishedAt')}
                    readOnly={![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)}
                    onChange={(val) => setFinishedAt(val)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    required
                    type="number"
                    label="Duración (En días)"
                    value={getContractFieldWithNews('contractPeriod')}
                    InputProps={{
                      readOnly: ![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)
                    }}
                    onChange={(evt) => setContractPeriod(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={dependency || null}
                    options={dependencyOpts}
                    getOptionLabel={inactiveLabel(allDependencyEntries(catalogCfg))}
                    onChange={(__, newValue) => {
                      setDependency(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Dependencia"
                        placeholder="Dependencia"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={nature || null}
                    options={natureOpts}
                    getOptionLabel={inactiveLabel(allNatureEntriesForCode(catalogCfg, contractCode))}
                    onChange={(__, newValue) => {
                      setNature(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={contractCode?.startsWith("DEX_") ? "Convenio (Naturaleza)" : "Naturaleza del contrato"}
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    fullWidth
                    value={modality || null}
                    options={modalityOpts}
                    getOptionLabel={inactiveLabel(allModalityEntries(catalogCfg))}
                    onChange={(__, newValue) => {
                      setModality(newValue);
                      // El tipo depende de la modalidad: se limpia si no pertenece a la nueva.
                      if (!newValue || !typesForModality(catalogCfg, newValue).includes(modalityContractType)) {
                        setModalityContractType(null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Modalidad de contratación"
                        placeholder="Modalidad de contratación"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <Autocomplete
                    readOnly={!canEditClassification}
                    disabled={!modality}
                    fullWidth
                    value={modalityContractType || null}
                    options={modalityTypeOpts}
                    getOptionLabel={inactiveLabel(allTypeEntriesForModality(catalogCfg, modality))}
                    onChange={(__, newValue) => {
                      setModalityContractType(newValue)
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de contrato de la modalidad"
                        placeholder="Tipo de contrato de la modalidad"
                      />
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <TextField
                    required
                    multiline
                    rows={5}
                    label="Objeto de contrato"
                    value={contractSubject}
                    InputProps={{
                      readOnly: ![CONTRACT_STATUS.DRAFT, CONTRACT_STATUS.SUPERVISOR].includes(status)
                    }}
                    onChange={(evt) => setContractSubject(evt.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                {!isSecopUrlValid ? (
                  <FormControl fullWidth>
                    <TextField
                      label="URL del SECOP"
                      value={secopUrl}
                      InputProps={{
                        readOnly: [CONTRACT_STATUS.CLOSED].includes(status)
                      }}                      
                      onChange={(evt) => setSecopUrl(evt.target.value)}
                      onBlur={handleSecopUrlBlur}
                    />
                  </FormControl>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Link
                      component="a"
                      href={secopUrl}
                      target="_blank"
                      sx={{ mr: 3, flex: 1, textDecoration: 'none', maxWidth: '100%' }}
                    >
                      Ver contrato en SECOP
                      <Box sx={{
                        fontSize: 10,
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>({secopUrl})</Box>
                    </Link>
                    <IconButton size="small" onClick={() => {
                      setSecopUrl('');
                      setIsSecopUrlValid(false);
                    }}>
                      <EditOutlined />
                    </IconButton>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12}>
                <AnnexesUploader
                  bucket={process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS}
                  referenceCode={contractCode}
                  annexes={annexes}
                  setAnnexes={setAnnexes}
                  disabled={!contractCode || [CONTRACT_STATUS.CLOSED].includes(getContractFieldWithNews('status'))}
                  defaultAnnexesList={annexesGroup.contract}
                />
              </Grid>
            </Grid>
            {[CONTRACT_STATUS.APPROVED, CONTRACT_STATUS.CLOSED].includes(status)
              && <Grid
                container
                spacing={2}
                sx={{ my: 2, borderTop: 'thin solid lightgray' }}
              >
                <Grid item xs={12}>
                  <ContractNews
                    disabled={!contractCode || [CONTRACT_STATUS.CLOSED].includes(getContractFieldWithNews('status'))}
                    setNews={setNews}
                    getContractFieldWithNews={getContractFieldWithNews}
                    contract={{
                      contractBudget,
                      finishedAt: getContractFieldWithNews('finishedAt'),
                      news,
                      startedAt,
                      tasks
                    }}
                  />
                </Grid>
              </Grid>
            }
            <Grid container spacing={1} sx={{ borderTop: 'thin solid lightgray', mt: 2 }}>
              <Grid item xs={12} sx={{ color: 'gray', my: 2 }}>Contratista</Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <SelectUser
                    noAdd={true}
                    disabled={
                      status !== CONTRACT_STATUS.DRAFT
                      && status !== CONTRACT_STATUS.SUPERVISOR
                    }
                    noedit={[
                      CONTRACT_STATUS.SUPERVISOR,
                      CONTRACT_STATUS.CLOSED,
                      CONTRACT_STATUS.SUSPENDED,
                      CONTRACT_STATUS.FINISHED
                    ].includes(getContractFieldWithNews('status'))}
                    userType="users"
                    placeholder="Contratista"
                    value={contractor}
                    setValue={setContractor}
                    onChange={(__, contratr) => {
                      setContractorNuip(contratr?.nuip)
                      setContractor(contratr);
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Button
                  disabled={
                    (!supervisorIDs.includes(user.userId) && !user.roles.includes('ADMIN')
                      && !isAssignedSupport)
                  }
                  onClick={() => {
                    let message;
                    if (!salary) message = 'Debe ingresar los honorarios mensual';
                    else if (!startedAt) message = 'Debe ingresar la fecha de inicio del contrato';
                    else if (!finishedAt) message = 'Debe ingresar la fecha de finalización del contrato';
                    else if (!contractBudget) message = 'Debe ingresar el valor del contrato';
                    if (message) {
                      setInfoSnack({ open: true, message, severity: 'warning' });
                    } else {
                      setShowPaymentPlanModal(true);
                    }
                  }}
                >
                  Plan de pagos a contratista
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  disabled={
                    (!supervisorIDs.includes(user.userId) && !user.roles.includes('ADMIN')
                      && !isAssignedSupport)
                  }
                  onClick={() => setShowTasksModal(true)}
                >
                  Obligaciones del contratista
                </Button>
              </Grid>
            </Grid>
            <Box sx={{ mt: 5, display: 'flex' }}>
              <Box sx={{ flex: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/contracts${user.roles.includes('SUPERVISOR_CONTRACT') ? '/supervisor': ''}`)}
                  disabled={modified}
                >
                  Regresar
                </Button>
                <Button
                  variant="contained"
                  sx={{ mx: 1 }}
                  onClick={handleSubmit}
                  disabled={status === CONTRACT_STATUS.CLOSED}
                >
                  {!contractId ? 'Crear Contrato' : 'Actualizar'}
                </Button>
                {contractId && status === CONTRACT_STATUS.DRAFT
                  && <Button
                    variant="contained"
                    onClick={() => setSendSupervisorInfo({ open: true, title: "¿Realmente deseas enviar este contrato al supervisor?" })}
                  >
                    {loading ? <CircularProgress size={16} /> : 'Enviar a supervisor'}
                  </Button>
                }
                {contractId && status === CONTRACT_STATUS.SUPERVISOR
                  && <>
                    <Button
                      variant="contained"
                      onClick={handleSendContractor}
                      disabled={!supervisorIDs.includes(user.userId) && !user.roles.includes('ADMIN')
                        && !isAssignedSupport}
                    >
                      {loading ? <CircularProgress size={16} /> : 'Enviar a contratista'}
                    </Button>
                    {(supervisorIDs.includes(user.userId) || isAssignedSupport)
                      && <Button
                        variant="text"
                        color="error"
                        sx={{ mx: 1 }}
                        onClick={() => setConfirmInfo({ open: true, title: "¿Realmente deseas devolver este contracto?" })}
                      >
                        {loading ? <CircularProgress size={16} /> : 'Devolver'}
                      </Button>
                    }
                  </>
                }
              </Box>
              {contractId
                && getContractFieldWithNews('status') === CONTRACT_STATUS.FINISHED
                && user.permissions.includes(PERMISSIONS.CONTRACT.CLOSE)
                && <Button
                  variant="contained"
                  color="warning"
                  onClick={() => setCloseContractInfo({ open: true, title: "¿Realmente deseas cerrar este contrato?" })}
                >
                  Cerrar Contrato
                </Button>
              }
            </Box>
          </Box>
        </Grid>
        <Grid item xs={6} sx={{ textAlign: "center" }}>
          <ContractFormPanel
            contractId={contractId}
            contractCode={contractCode}
            designationUrl={designationUrl}
            fileUrl={fileUrl}
            handleGetDataStartup={handleGetDataStartup}
            liquidationUrl={liquidationUrl}
            loading={loading}
            setDesignationUrl={setDesignationUrl}
            setDocumentText={setDocumentText}
            setFileUrl={setFileUrl}
            setFileHashes={setFileHashes}
            setLiquidationUrl={setLiquidationUrl}
            setLoading={setLoading}
            setStartupUrl={setStartupUrl}
            startupUrl={startupUrl}
            status={getContractFieldWithNews('status')}
            setInfoSnack={setInfoSnack}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ContractForm;
